import { useEffect, useRef } from 'react';
import * as Tone from 'tone';
import { TIME_SIGNATURES } from '../config/timeSignatures';
import type { 
  DrumInstrument, 
  DrumGridData, 
  TimeSignature, 
  NoteDivision, 
  PlaybackPlayers,
  GrooveType,
  HitLevel
} from '../types';

const SLOW_TEMPO_THRESHOLD = 75; // 低於此BPM被視為慢速
const FAST_RESPONSE_PATTERNS = ['chillhopGroove2', 'chillhopGroove1', 'neosoul']; // 需要快速響應的pattern

const HIT_VOLUME_MAP: Record<string, number> = {
  x: 0,
  X: 3,
  g: -9,
  f: 0,
};

const FLAM_OFFSET = Tone.Time('32n').toSeconds();

// Groove 计算函数
const calculateGrooveTiming = (
  stepIndex: number, 
  grooveType: GrooveType, 
  shuffleAmount: number, 
  swingRatio: number, 
  stepsPerBar: number
): number => {
  // 确保 normal 模式完全没有任何偏移
  if (!grooveType || grooveType === 'normal') {
    return 0;
  }
  
  const isEighthNoteGrid = stepsPerBar === 8;
  const isSixteenthNoteGrid = stepsPerBar === 16;
  
  if (grooveType === 'shuffle' && (isEighthNoteGrid || isSixteenthNoteGrid)) {
    // Shuffle: 延迟奇数步骤 (off-beat)
    if (stepIndex % 2 === 1) {
      return shuffleAmount * 0.1; // 最大延迟100ms
    }
  }
  
  if (grooveType === 'swing' && (isEighthNoteGrid || isSixteenthNoteGrid)) {
    // Swing: 根据swing ratio调整奇数拍
    const beatPosition = stepIndex % (isEighthNoteGrid ? 2 : 4);
    if (beatPosition === 1 || (isSixteenthNoteGrid && beatPosition === 3)) {
      const baseTime = Tone.Time('16n').toSeconds();
      const swingTime = baseTime * swingRatio;
      const straightTime = baseTime * 0.5;
      return swingTime - straightTime;
    }
  }
  
  return 0;
};

interface UsePlaybackOptions {
  isPlaying: boolean;
  setIsPlaying: (playing: boolean) => void;
  isPaused: boolean;
  setIsPaused: (paused: boolean) => void;
  step: number;
  setStep: (step: number) => void;
  grid: DrumGridData;
  instruments: DrumInstrument[];
  players: PlaybackPlayers;
  tempo: number;
  noteDivision: NoteDivision;
  timeSignature: TimeSignature;
  stepsPerBar: number;
  loopCount: number;
  iosAudioUnlocked: boolean;
  showDynamicMessage?: (message: string, type?: "info" | "success" | "warning" | "error", duration?: number) => void;
  t?: (key: string) => string;
  setIsDemoMode?: (demo: boolean) => void;
  setIsAudioLoading?: (loading: boolean) => void;
  grooveType?: GrooveType;
  shuffleAmount?: number;
  swingRatio?: number;
}

interface UsePlaybackReturn {
  togglePlayback: () => Promise<void>;
  stopPlayback: () => void;
  resetStep: () => void;
}

export default function usePlayback({
  isPlaying,
  setIsPlaying,
  isPaused,
  setIsPaused,
  step,
  setStep,
  grid,
  instruments,
  players,
  tempo,
  noteDivision,
  timeSignature,
  stepsPerBar,
  loopCount,
  iosAudioUnlocked,
  showDynamicMessage,
  t,
  setIsDemoMode,
  setIsAudioLoading,
  grooveType = 'normal',
  shuffleAmount = 0.2,
  swingRatio = 0.67
}: UsePlaybackOptions): UsePlaybackReturn {
  const stepRef = useRef<number>(0);
  const gridRef = useRef<DrumGridData>(grid);
  const playersRef = useRef<PlaybackPlayers>(players);
  const loopCountRef = useRef<number>(0);

  const stopAllPlayers = (): void => {
    Object.values(playersRef.current).forEach((player) => {
      if (player) {
        try {
          // 對慢速pattern優化：立即停止，不等待fadeOut
          if (tempo <= SLOW_TEMPO_THRESHOLD) {
            player.stop(0); // 立即停止
          } else {
            player.stop();
          }
          // 清理所有已安排的事件
          if (player.cancel) {
            player.cancel();
          }
        } catch (error) {
          // 靜默處理停止錯誤
          console.warn('Player stop error:', (error as Error).message);
        }
      }
    });
  };

  useEffect(() => {
    gridRef.current = grid;
  }, [grid]);

  useEffect(() => {
    playersRef.current = players;
  }, [players]);

  useEffect(() => {
    if (!isPlaying) return;

    loopCountRef.current = 0;

    const repeat = (time: number): void => {
      const currentStep = stepRef.current;
      const currentGrid = gridRef.current;
      const currentPlayers = playersRef.current;

      instruments.forEach(({ id }) => {
        const hit = currentGrid[id]?.[currentStep];
        const player = currentPlayers[id];
        if (hit && player?.buffer?.loaded) {
          try {
            // 確保停止任何正在進行的播放
            player.stop();
            
            // 計算groove時間偏移
            const grooveOffset = calculateGrooveTiming(currentStep, grooveType, shuffleAmount, swingRatio, stepsPerBar);
            const playTime = time + grooveOffset;
            
            // 設置音量
            player.volume.value = hit ? (HIT_VOLUME_MAP[hit] ?? 0) : 0;
            
            if (hit === 'f') {
              // Flam效果：兩個快速連續的hit
              player.volume.value = -6;
              player.start(playTime);
              // 創建第二個音檔實例來避免時間線衝突
              setTimeout(() => {
                if (player?.buffer?.loaded) {
                  try {
                    player.volume.value = 0;
                    player.start();
                  } catch (flamError) {
                    console.warn(`Flam effect failed for ${id}:`, flamError);
                  }
                }
              }, (FLAM_OFFSET + grooveOffset) * 1000); // 轉換為毫秒
            } else {
              player.start(playTime);
            }
          } catch (error) {
            // 靜默處理時間線錯誤，避免中斷播放
            console.warn(`Player start error for ${id}:`, (error as Error).message);
            // 嘗試立即播放作為後備
            try {
              player.stop();
              player.start();
            } catch (fallbackError) {
              console.warn(`Fallback start failed for ${id}:`, fallbackError);
            }
          }
        }
      });

      setStep(currentStep);
      const nextStep = (currentStep + 1) % stepsPerBar;
      stepRef.current = nextStep;

      if (stepRef.current === 0) {
        loopCountRef.current++;
        if (loopCount > 0 && loopCountRef.current >= loopCount) {
          Tone.Transport.stop();
          Tone.Transport.cancel();
          setIsPlaying(false);
          setIsPaused(false);
          setStep(0);
          stepRef.current = 0;
          loopCountRef.current = 0;
        }
      }
    };

    // 安全地重設Transport
    try {
      Tone.Transport.cancel();
      Tone.Transport.stop();
      
      Tone.Transport.bpm.value = tempo;

      const stepTime = Tone.Time(noteDivision).toSeconds();
      const startPosition = Tone.Time(stepRef.current * stepTime).toBarsBeatsSixteenths();

      Tone.Transport.position = startPosition;
      
      // 安全地安排重複事件
      const repeatId = Tone.Transport.scheduleRepeat(repeat, noteDivision);
      
      // 稍微延遲開始，確保所有設置完成
      Tone.Transport.start('+0.02');
      
      return () => {
        try {
          Tone.Transport.stop();
          Tone.Transport.cancel();
          if (repeatId) {
            Tone.Transport.clear(repeatId);
          }
        } catch (cleanupError) {
          console.warn('Transport cleanup error:', cleanupError);
        }
      };
    } catch (transportError) {
      console.error('Transport setup error:', transportError);
      return () => {}; // 返回空的清理函數
    }
  }, [isPlaying, tempo, noteDivision, stepsPerBar, loopCount, instruments, grooveType, shuffleAmount, swingRatio]);

  const togglePlayback = async (): Promise<void> => {
    if (/iPhone|iPad|iPod/.test(navigator.userAgent) && !iosAudioUnlocked) {
      showDynamicMessage && showDynamicMessage(t?.('unlockIosAudio') || 'Unlock iOS Audio', 'error', 4000);
      return;
    }

    try {
      // 如果正在播放，立即暫停（優化響應速度）
      if (isPlaying) {
        setIsPlaying(false);
        setIsPaused(true);
        Tone.Transport.stop();
        Tone.Transport.cancel();
        stopAllPlayers();
        return; // 立即返回，不需要等待其他操作
      }

      // 只有在開始播放時才進行音頻系統檢查
      if (Tone.context.state !== 'running') {
        await Tone.start();
        if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
          await new Promise((resolve) => setTimeout(resolve, 100)); // 減少iOS延遲
        }
      }

      // 優化音頻載入檢查：智能載入策略
      setIsAudioLoading && setIsAudioLoading(true);
      
      const loadingPromises: Promise<void>[] = [];
      let hasLoadedPlayers = false;
      let totalPlayers = 0;
      let loadedPlayers = 0;
      
      instruments.forEach(({ id }) => {
        const player = players[id];
        if (player) {
          totalPlayers++;
          if (player.buffer?.loaded) {
            hasLoadedPlayers = true;
            loadedPlayers++;
          } else {
            const loadPromise = new Promise<void>((resolve) => {
              const timeout = setTimeout(() => {
                console.warn(`⏰ Player ${id} load timeout, continuing anyway`);
                resolve();
              }, tempo <= SLOW_TEMPO_THRESHOLD ? 1000 : 1500); // 慢節拍pattern更短的等待時間
              
              const checkLoaded = (): void => {
                if (player.buffer?.loaded) {
                  clearTimeout(timeout);
                  hasLoadedPlayers = true;
                  loadedPlayers++;
                  resolve();
                } else {
                  setTimeout(checkLoaded, 30); // 進一步減少檢查間隔
                }
              };
              checkLoaded();
            });
            loadingPromises.push(loadPromise);
          }
        }
      });

      // 智能等待策略：如果有一半播放器已載入，或者有基本的kick+snare，就可以開始播放
      const hasEssentialPlayers = instruments.some(({ id, type }) => 
        (type === 'kick' || type === 'snare') && players[id]?.buffer?.loaded
      );
      
      if (loadingPromises.length > 0 && !hasLoadedPlayers && !hasEssentialPlayers) {
        await Promise.race([
          Promise.all(loadingPromises),
          new Promise<void>((resolve) => setTimeout(resolve, tempo <= SLOW_TEMPO_THRESHOLD ? 1500 : 2000)),
        ]);
      }
      
      // 給用戶友好的提示
      if (loadedPlayers < totalPlayers && showDynamicMessage) {
        showDynamicMessage(
          `${loadedPlayers}/${totalPlayers} audio files loaded. Playing with available sounds...`,
          'info',
          2000
        );
      }

      // iOS設備測試邏輯優化
      if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
        const workingPlayers = instruments.filter(({ id }) => {
          const player = players[id];
          return player && player.buffer?.loaded;
        });
        if (workingPlayers.length === 0) {
          showDynamicMessage &&
            showDynamicMessage(
              (t?.('audioFilesLoadingMessage') || 'Audio files loading') + '\n\n' + (t?.('audioFilesLoadingInstructions') || 'Please wait...'),
              'error',
              4000 // 減少顯示時間
            );
          return;
        }
        // 移除測試音檔播放，直接開始播放
      }

      setIsPlaying(true);
      if (isPaused) {
        setIsPaused(false);
      } else {
        stepRef.current = 0;
        setStep(0);
      }
    } catch (error) {
      const errorMessage = (error as Error).message;
      if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
        if ((error as Error).name === 'NotAllowedError' || errorMessage.includes('gesture')) {
          showDynamicMessage &&
            showDynamicMessage(
              (t?.('iosAudioBlocked') || 'iOS Audio Blocked') + '\n\n' + (t?.('iosAudioBlockedInstructions') || 'Please tap to enable audio'),
              'error',
              4000 // 減少顯示時間
            );
        } else if (errorMessage.includes('network') || errorMessage.includes('loading')) {
          showDynamicMessage &&
            showDynamicMessage(
              (t?.('audioLoadFailed') || 'Audio Load Failed') + '\n\n' + (t?.('audioLoadFailedInstructions') || 'Please try again'),
              'error',
              4000 // 減少顯示時間
            );
        } else {
          showDynamicMessage &&
            showDynamicMessage(
              `${t?.('playbackFailed') || 'Playback Failed'}: ${errorMessage}\n\n${t?.('playbackFailedInstructions') || 'Please try again'}`,
              'error',
              3000 // 減少顯示時間
            );
        }
      } else {
        showDynamicMessage &&
          showDynamicMessage(`${t?.('playbackError') || 'Playback Error'}: ${errorMessage}`, 'error');
      }
    } finally {
      setIsAudioLoading && setIsAudioLoading(false);
    }
  };

  const stopPlayback = (): void => {
    if (!isPlaying && !isPaused) return;
    
    // 立即停止播放狀態，提高響應速度
    setIsPlaying(false);
    setIsPaused(false);
    setStep(0);
    stepRef.current = 0;
    loopCountRef.current = 0;
    if (setIsDemoMode) setIsDemoMode(false);
    
    // 停止Transport和所有播放器
    Tone.Transport.stop();
    Tone.Transport.cancel();
    stopAllPlayers();
    
    // 簡化Transport重設，只設定必要參數
    Tone.Transport.position = '0:0:0';
    // 移除不必要的timeSignature和tempo重設，因為這些在播放時會重新設定
  };

  const resetStep = (): void => {
    stepRef.current = 0;
    setStep(0);
  };

  return { togglePlayback, stopPlayback, resetStep };
} 